import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Avatar,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setProfileLinkPosition } from "../../store/slices/animationSlice";
import { auth } from "../../services/firebase/config";
import { signOut } from "firebase/auth";
import NfcIcon from "@mui/icons-material/Nfc";
import HomeIcon from "@mui/icons-material/Home";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalGroceryStoreIcon from "@mui/icons-material/LocalGroceryStore";
import PersonIcon from "@mui/icons-material/Person";
import PoolIcon from "@mui/icons-material/Pool";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";

const Navbar = () => {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useSelector((state) => state.auth.isAdmin);
  const dispatch = useDispatch();
  const profileLinkRef = useRef(null);
  const [adminAnchorEl, setAdminAnchorEl] = useState(null);
  const [marketplaceAnchorEl, setMarketplaceAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleAdminMenuOpen = (event) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminAnchorEl(null);
  };

  const handleMarketplaceMenuOpen = (event) => {
    setMarketplaceAnchorEl(event.currentTarget);
  };

  const handleMarketplaceMenuClose = () => {
    setMarketplaceAnchorEl(null);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
    if (profileLinkRef.current) {
      const rect = profileLinkRef.current.getBoundingClientRect();
      console.log("Navbar - Profile Link Rect (on menu open):", rect);
      dispatch(setProfileLinkPosition({ x: rect.left, y: rect.top }));
    }
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          mb: 1,
        }}
      >
        <Link to="/">
          <img
            src="/logo.png"
            alt="my neighbor logo"
            style={{
              maxWidth: 400,
              width: "80%",
              height: "auto",
              display: "block",
              margin: "0 auto",
            }}
          />
        </Link>
      </Box>
      <Box sx={{ mt: 2 }}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: "#f2f0e4", // Cream background
            color: "#000000",
            boxShadow: "none",
            borderBottom: "2px solid #000000",
            borderTop: "2px solid #000000",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            {/* Pool - left of Marketplace */}
            <Button
              color="inherit"
              component={Link}
              to="/pool"
              sx={{
                color: "#000000",
                fontSize: "0.9rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PoolIcon sx={{ fontSize: "1.5rem" }} />
            </Button>

            {/* Center Left - MARKETPLACE */}
            <Button
              color="inherit"
              component={Link}
              to="/marketplace"
              sx={{
                color: "#000000",
                fontSize: "0.9rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <StorefrontIcon sx={{ fontSize: "1.5rem" }} />
            </Button>

            {/* Center Right - GROCERY STORE */}
            <Button
              color="inherit"
              component={Link}
              to="/grocery-store"
              sx={{
                color: "#000000",
                fontSize: "0.9rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <LocalGroceryStoreIcon sx={{ fontSize: "1.5rem" }} />
            </Button>

            {/* Right side - PROFILE/SIGN IN, ADMIN, SIGN OUT */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* PROFILE/SIGN IN button */}
              {user ? (
                <>
                  <Button
                    ref={profileLinkRef}
                    color="inherit"
                    onClick={handleProfileMenuOpen}
                    sx={{
                      color: "#000000",
                      fontSize: "0.9rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: "1.5rem" }} />
                  </Button>
                  <Menu
                    anchorEl={profileAnchorEl}
                    open={Boolean(profileAnchorEl)}
                    onClose={handleProfileMenuClose}
                    PaperProps={{
                      sx: {
                        backgroundColor: "#f2f0e4",
                        border: "1px solid #000000",
                        borderRadius: 0,
                      },
                    }}
                  >
                    <MenuItem
                      component={Link}
                      to="/profile"
                      onClick={handleProfileMenuClose}
                    >
                      <ListItemText primary="View Profile" />
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/edit-profile"
                      onClick={handleProfileMenuClose}
                    >
                      <ListItemText primary="Edit Profile" />
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleSignOut}>
                      <ListItemText primary="Sign Out" />
                      <LogoutIcon fontSize="small" sx={{ ml: 1 }} />
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  color="inherit"
                  component={Link}
                  to="/signin"
                  sx={{
                    color: "#000000",
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <PersonIcon sx={{ fontSize: "1.5rem" }} />
                </Button>
              )}

              <Menu
                anchorEl={profileAnchorEl}
                open={Boolean(profileAnchorEl)}
                onClose={handleProfileMenuClose}
                PaperProps={{
                  sx: {
                    backgroundColor: "#f2f0e4",
                    border: "1px solid #000000",
                    borderRadius: 0,
                  },
                }}
              >
                <MenuItem
                  component={Link}
                  to="/profile"
                  onClick={handleProfileMenuClose}
                >
                  <ListItemText primary="View Profile" />
                </MenuItem>
                <MenuItem
                  component={Link}
                  to="/edit-profile"
                  onClick={handleProfileMenuClose}
                >
                  <ListItemText primary="Edit Profile" />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleSignOut}>
                  <ListItemText primary="Sign Out" />
                  <LogoutIcon fontSize="small" sx={{ ml: 1 }} />
                </MenuItem>
              </Menu>

              {/* ADMIN button - only visible for admins */}
              {isAdmin && (
                <>
                  <Button
                    color="inherit"
                    onClick={handleAdminMenuOpen}
                    sx={{
                      color: "#000000",
                      fontSize: "0.9rem",
                      ml: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <AdminPanelSettingsIcon sx={{ fontSize: "1.5rem" }} />
                    ADMIN
                  </Button>
                  <Menu
                    anchorEl={adminAnchorEl}
                    open={Boolean(adminAnchorEl)}
                    onClose={handleAdminMenuClose}
                    PaperProps={{
                      sx: {
                        backgroundColor: "#f2f0e4",
                        border: "1px solid #000000",
                        borderRadius: 0,
                      },
                    }}
                  >
                    <MenuItem
                      component={Link}
                      to="/admin/users"
                      onClick={handleAdminMenuClose}
                    >
                      <ListItemText primary="User Management" />
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/admin/homes"
                      onClick={handleAdminMenuClose}
                    >
                      <ListItemText primary="Home Management" />
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/admin/items"
                      onClick={handleAdminMenuClose}
                    >
                      <ListItemText primary="Item Management" />
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/admin/nfc"
                      onClick={handleAdminMenuClose}
                    >
                      <ListItemText primary="NFC Card Management" />
                      <NfcIcon fontSize="small" sx={{ ml: 1 }} />
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/admin/grocery-items"
                      onClick={handleAdminMenuClose}
                    >
                      <ListItemText primary="Grocery Store Management" />
                      <LocalGroceryStoreIcon fontSize="small" sx={{ ml: 1 }} />
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </Box>
    </>
  );
};

export default Navbar;
